import { useEffect, useMemo, useState } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { t } from "@/i18n-js/instance";
import { classNames } from "@/react/helpers/twMergeWithCN";
import { useIsPropertiesPanelRevampEnabled } from "@circle-react-shared/BuilderUI/Hooks/useIsPropertiesPanelRevamp";
import { Dropdown } from "@circle-react-shared/uikit/Dropdown";
import { Form } from "@circle-react-uikit/Form";
import { FormItemWrapper } from "../../DefaultEditorV2/FormItemWrapper";
import { AddPropertyDropdown } from "./AddPropertyDropdown";
import { RemovePropertyButton } from "./RemovePropertyButton";
import { UNITS, UNIT_CONSTRAINTS } from "./constants";
import type { Unit } from "./constants";
import { clampValue } from "./utils/clampValue";
import { parseDimension } from "./utils/parseDimension";

interface DimensionInputProps {
  isHidden?: boolean;
  label: string;
  name: string;
  onRemove?: () => void;
  options?: Array<{ label: string; value: string; onClick?: () => void }>;
  units?: Unit[];
}

export const DimensionInput = ({
  label,
  name,
  units = UNITS,
  options = [],
  isHidden = false,
  onRemove,
}: DimensionInputProps) => {
  const { control } = useFormContext();
  const rawValue = useWatch({ control, name }) as string | undefined;
  const isPropertiesPanelV2 = useIsPropertiesPanelRevampEnabled();

  const parsed = useMemo(() => parseDimension(rawValue), [rawValue]);

  const [numericValue, setNumericValue] = useState(parsed.numeric);
  const [unit, setUnit] = useState<Unit>(parsed.unit);

  const isAuto = unit === "auto";
  const isNone = unit === "none";
  const constraints = UNIT_CONSTRAINTS[unit] ?? {};

  useEffect(() => {
    setNumericValue(parsed.numeric);
    setUnit(parsed.unit);
  }, [parsed.numeric, parsed.unit]);

  const validateValue = () => {
    if (isAuto || (isPropertiesPanelV2 && isNone)) {
      return true;
    }

    if (!numericValue && numericValue !== "0") {
      return t("builder_ui.dimension_input.errors.value_required");
    }

    const num = parseFloat(numericValue);
    if (isNaN(num)) {
      return t("builder_ui.dimension_input.errors.invalid_number");
    }

    if (constraints.min && num < parseFloat(constraints.min)) {
      return t("builder_ui.dimension_input.errors.value_too_small");
    }

    if (constraints.max && num > parseFloat(constraints.max)) {
      return t("builder_ui.dimension_input.errors.value_exceeds_max");
    }

    return true;
  };

  const handleInputChange = (
    value: string,
    currentUnit: Unit,
    onChange: (val: string) => void,
  ) => {
    setNumericValue(value);

    if (currentUnit === "auto" || currentUnit === "none") {
      onChange(currentUnit);
      return;
    }

    const num = parseFloat(value);
    if (!isNaN(num)) {
      onChange(`${num}${currentUnit}`);
    }
  };

  const handleUnitChange = (
    nextUnit: Unit,
    currentValue: string,
    onChange: (val: string) => void,
  ) => {
    setUnit(nextUnit);

    if (nextUnit === "auto" || nextUnit === "none") {
      onChange(nextUnit);
      return;
    }

    const clampedValue = clampValue(
      currentValue,
      UNIT_CONSTRAINTS[nextUnit] ?? {},
    );
    setNumericValue(clampedValue);
    onChange(`${clampedValue}${nextUnit}`);
  };

  const unitDropdownOptions = useMemo(
    () =>
      units.map(unit => ({
        label: unit,
        value: unit,
      })),
    [units],
  );
  const shouldShowAddButton = options.length > 0;
  const shouldShowRemoveButton = !!onRemove;

  if (isPropertiesPanelV2) {
    return (
      <Form.Item
        name={name}
        label={label}
        hideBorder
        hideDescription
        className={classNames("!grid grid-cols-3 !items-center !p-0", {
          "!hidden": isHidden,
        })}
        childrenWrapper={FormItemWrapper}
        labelWrapperClassName="inline-flex !min-w-16 !w-16"
        labelClassName="!font-normal text-xs mb-0 mr-2"
        isFirstChild
      >
        <Controller
          name={name}
          control={control}
          rules={{ validate: validateValue }}
          render={({ field: { onChange } }) => (
            <div className="flex w-full items-center gap-2.5">
              <div className="border-primary group relative w-full overflow-hidden rounded-lg border">
                <input
                  type="number"
                  value={isAuto || isNone ? "" : numericValue}
                  onChange={e =>
                    handleInputChange(e.target.value, unit, onChange)
                  }
                  disabled={isAuto || isNone}
                  min={constraints.min}
                  max={constraints.max}
                  // Design specs use 40px height, but the components under uikit are 44px.
                  // The replace py-2 with py-2.5 to keep the UI consistent, despite not being
                  // accurate with the Builder designs.
                  className="bg-primary text-dark group-hover:border-darkest focus:border-darkest w-full appearance-none border-none py-2.5 pl-4 pr-3 text-left [-moz-appearance:textfield] focus:!outline-none focus:!ring-0 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                  aria-label={label}
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-2">
                  {units.length > 1 ? (
                    <Dropdown
                      buttonClassName="!flex !items-center"
                      buttonWrapperClassName="!flex !items-center"
                      menuItemsClassName="!py-1 !px-1 !w-auto flex flex-col gap-0.5 rounded-xl"
                      button={
                        <span className="text-default hover:bg-tertiary rounded p-1 text-xs leading-none transition-colors">
                          {unit}
                        </span>
                      }
                    >
                      {unitDropdownOptions.map(option => (
                        <Dropdown.ItemLink
                          key={option.value}
                          className="text-light hover:bg-tertiary rounded !p-1 text-center text-xs font-normal"
                          onClick={() =>
                            handleUnitChange(
                              option.value,
                              numericValue,
                              onChange,
                            )
                          }
                          forceClose
                        >
                          {option.label}
                        </Dropdown.ItemLink>
                      ))}
                    </Dropdown>
                  ) : (
                    <span className="text-default p-1 text-xs leading-none">
                      {unit}
                    </span>
                  )}
                </div>
              </div>
              {shouldShowAddButton && <AddPropertyDropdown options={options} />}
              {shouldShowRemoveButton && (
                <RemovePropertyButton
                  onClick={() => {
                    handleUnitChange("none", numericValue, onChange);
                    onRemove();
                  }}
                />
              )}
            </div>
          )}
        />
      </Form.Item>
    );
  }

  return (
    <Form.Item
      name={name}
      label={label}
      hideBorder
      hideDescription
      labelClassName="!font-normal text-xs"
      isFirstChild
    >
      <Controller
        name={name}
        control={control}
        rules={{ validate: validateValue }}
        render={({ field: { onChange } }) => (
          <div className="group relative">
            <input
              type="number"
              value={isAuto ? "" : numericValue}
              onChange={e => handleInputChange(e.target.value, unit, onChange)}
              disabled={isAuto}
              min={constraints.min}
              max={constraints.max}
              className="border-primary bg-primary text-dark group-hover:border-darkest focus:border-darkest w-full appearance-none rounded-lg border py-2 pl-4 pr-3 text-left focus:!outline-none focus:!ring-0 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
              aria-label={label}
            />

            <div className="absolute inset-y-0 right-0 flex items-center pr-2">
              {units.length > 1 ? (
                <select
                  value={unit}
                  onChange={e =>
                    handleUnitChange(
                      e.target.value as Unit,
                      numericValue,
                      onChange,
                    )
                  }
                  className="text-default w-full appearance-none rounded-lg border-none bg-transparent py-2 pl-3 pr-2 text-center [background-image:none] focus:!outline-none focus:!ring-0"
                >
                  {units.map(unitOption => (
                    <option key={unitOption} value={unitOption}>
                      {unitOption}
                    </option>
                  ))}
                </select>
              ) : (
                <span className="text-default py-2 pl-3 pr-2 text-center text-sm">
                  {units[0]}
                </span>
              )}
            </div>
          </div>
        )}
      />
    </Form.Item>
  );
};
